import { useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import type { Query } from '@/api/query'
import { useBrand } from '@/brand/context'
import { saveTextFile } from '@/lib/download'
import { formatDateTime } from '@/lib/format'
import { idempotencyKey } from '@/lib/idempotency'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { summariseTarget } from '@/settings/publish-target/summary'

type Publication = Schema<'PublicationOut'>
type TargetQuery = Query<Schema<'PublishTargetOut'> | null>
type Document = Schema<'ContentDocumentOut'>

/** The tenant's time zone is the only one this product renders in (CLAUDE.md). */
const IST = 'Asia/Kolkata'

/**
 * The statuses a document has to have reached before publishing is a question
 * worth asking about it. `approved` and `published` are `PUBLISHABLE_STATUSES`
 * on the server; `publishing` is the state the press moves a document into
 * while the worker is running, and `failed` is where a publish that did not
 * work leaves it — both belong on a screen whose whole subject is what did and
 * did not reach the tenant's website.
 */
const SHIPPABLE = ['approved', 'publishing', 'published', 'failed'] as const

/**
 * What `GET /seo/sites/{id}/content` will return at most, from `openapi.json`,
 * where `default` and `maximum` are both 100 — so this is the cap in force even
 * though the call sends no `limit`. Named on screen because the route has no
 * cursor, and a published list that quietly stops at 100 reads as "we published
 * 100 things" (WEB-16).
 */
const DOCUMENT_LIMIT = 100

/**
 * The documents that have reached a publishable state, for one site.
 *
 * Its own hook because two things on the screen read it — the failure alert in
 * the right-hand column (SEO-UI-70) and the list at the bottom — and they have
 * to be one answer. `null` for `siteId` resolves to no data rather than not
 * calling the hook, because a hook cannot be conditional.
 */
function useDocuments(siteId: string | null) {
  return useQuery(
    (signal) =>
      siteId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/sites/{site_id}/content', {
              params: { path: { site_id: siteId }, query: { status: [...SHIPPABLE] } },
              signal,
            }),
          ),
    [siteId],
  )
}

/* --- the screen ---------------------------------------------------------------- */

/**
 * Where we publish — S-D5's `seo.html#targets`, built in S-G5 over S-B28.
 *
 * **The five endpoints in `app/v1/seo_publish.py` had no caller at all.** They
 * shipped tested and reachable, and until this screen existed the approval queue
 * told the tenant in as many words that *"pushing it to your website is a
 * separate step and is not built yet"* — audit row §A1, and §V3 as the tenant
 * experienced it.
 *
 * WHY IT IS A ROUTE UNDER `/seo` AND NOT A NINTH SETTINGS PANEL
 * ---------------------------------------------------------------
 * The same argument `RunTriggers` makes: **a publish target is per site and the
 * settings screen is per organisation.** An agency with a staging domain and a
 * client's live domain needs two different answers, and `sites.publish_target`
 * is the column that says so. S-D3 approved eight settings panels and eight SEO
 * sections; adding a ninth of either would be inventing information architecture
 * nobody drew, which is the reason `panels.ts` folded webhooks into
 * Notifications rather than adding a row.
 *
 * So it has **no sidebar entry** and two ways in instead, which is what the
 * audit's correction to the brief asks for: a card in Settings → Connections,
 * because that is where a tenant looks for *"what is AugRank connected to"* and
 * where `configured_target`'s own refusal sends them; and a link from the
 * approval queue, because that is the moment the question actually arises.
 *
 * WHAT IS NEVER ON THIS SCREEN
 * ------------------------------
 * **A credential, in any form.** Not masked, not a prefix, not a length. CLAUDE.md's
 * Secrets invariant is absolute and `PublishTargetOut` is built for it: a
 * `credential_set` boolean and the Parameter Store path, which is an address
 * rather than a secret. The field below is write-only — it is empty on arrival,
 * empty after a save, and omitted from the request when the tenant did not type
 * in it, which is what `PublishTargetIn.credential` means by *"omit to leave the
 * stored one unchanged"*.
 *
 * **Two mockup controls are deliberately absent**, because no endpoint stands
 * behind either and half of one is worse than none. *Send a test post* would
 * have to publish something real to somebody's live site. *Disconnect* would
 * have to delete a target and its credential, and `write_target` only ever
 * merges — there is no DELETE. Both are named in `PROGRESS.md` rather than
 * rendered as buttons that do nothing.
 */
export function PublishTargets(): JSX.Element {
  const brand = useBrand()
  const { membership } = useActiveTenant()
  const publishable = may(membership, 'content:approve')
  const [siteId, setSiteId] = useState<string | null>(null)

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])
  const listed = sites.state.data?.sites ?? []
  const selectedId = siteId ?? listed[0]?.id ?? null
  const site = listed.find((row) => row.id === selectedId) ?? null

  // SEO-UI-70. Lifted out of `PublishedWork`, which used to own it: the failure
  // alert the design puts in the right-hand column and the list at the bottom
  // of the screen are two readings of one answer, and fetching it twice would
  // be two requests that can disagree with each other on screen.
  const documents = useDocuments(selectedId)
  // The target, read once here rather than inside `TargetReadout`: the list at
  // the bottom needs its kind too — a *download* target's whole point is the
  // file, and until 2026-09-20 the row offered it only inside the expanded
  // publishing history, behind a button that did not say a file was there.
  const target = useQuery(
    (signal) =>
      selectedId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/publish-targets/{site_id}', {
              params: { path: { site_id: selectedId } },
              signal,
            }),
          ),
    [selectedId],
  )

  if (sites.state.status === 'loading') return <Loading label="Loading your sites" />
  if (sites.state.status === 'error') {
    return <ErrorState failure={sites.state.failure} onRetry={sites.refetch} />
  }

  const rows = documents.state.data?.documents ?? []

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <h1 className="ar-h1">Where we publish</h1>
        <span className="app-spacer" />
        {listed.length > 1 ? (
          <select
            // SEO-UI-49's fix, which named this screen. Without it `.ar-select`
            // is `width: 100%` inside a wrapping `.ar-row` and takes the whole
            // line, putting the heading and the picker on two rows.
            className="ar-select app-page-select"
            aria-label="Which site"
            value={selectedId ?? ''}
            onChange={(event) => {
              setSiteId(event.target.value)
            }}
          >
            {listed.map((row) => (
              <option key={row.id} value={row.id}>
                {row.domain}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {site === null ? (
        <div className="ar-card">
          <div className="ar-card__body">
            <Empty
              title="No site yet"
              body={`Add your website first. Until ${brand.productName} knows where your pages live there is nowhere for an approved article to go.`}
            />
          </div>
        </div>
      ) : (
        <>
          <TargetReadout
            key={site.id}
            siteId={site.id}
            target={target}
            beside={
              <LastFailure
                documents={rows}
                publishable={publishable}
                onRetried={() => {
                  documents.refetch()
                }}
              />
            }
          />
          <PublishedWork
            key={`${site.id}-published`}
            documents={documents}
            publishable={publishable}
            targetKind={target.state.data?.target ?? null}
          />
        </>
      )}
    </div>
  )
}

/* --- the configuration --------------------------------------------------------- */

/**
 * The target as this page shows it since the settings consolidation of
 * 2026-09-15: read-only, with the way to `/settings/publish-target`, which
 * carries the site so the panel opens on it. The setup card that lived here is
 * `settings/publish-target/TargetSetup.tsx`. `beside` is the failure alert, for
 * the reason SEO-UI-97 gave when it sat beside the setup card: it is derived
 * from the document list this page owns.
 */
function TargetReadout({
  siteId,
  target,
  beside,
}: {
  siteId: string
  target: TargetQuery
  beside: ReactNode
}): JSX.Element {
  const brand = useBrand()
  const summary =
    target.state.status === 'ready' && target.state.data !== null
      ? summariseTarget(target.state.data, brand.productName)
      : null

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>Where approved articles go</h2>
        <span className="ar-card__spacer" />
        <Link
          className="ar-btn ar-btn--ghost ar-btn--sm"
          to={`/settings/publish-target?site=${encodeURIComponent(siteId)}`}
        >
          Change in Settings →
        </Link>
      </div>
      <div className="ar-card__body ar-stack">
        {target.state.status === 'loading' ? (
          <Loading label="Loading the target" lines={1} />
        ) : null}
        {target.state.status === 'error' ? (
          <ErrorState failure={target.state.failure} onRetry={target.refetch} />
        ) : null}
        {target.state.status === 'ready' ? (
          summary === null ? (
            <p className="ar-subtle" style={{ margin: 0 }}>
              Not set up — approved articles have nowhere to go yet.
            </p>
          ) : (
            <p style={{ margin: 0 }}>{summary}</p>
          )
        ) : null}
        {beside}
      </div>
    </section>
  )
}

/**
 * The most recent document a publish left `failed`, and a way to try again.
 *
 * **SEO-UI-70.** A failure was visible only twice, and both were places nobody
 * looks until they already suspect something: a `Failed` badge on a row in the
 * list at the bottom of the screen, and `last_error` inside that row's
 * publishing history, which has to be expanded. The design puts a screen-level
 * alert in the right-hand column instead, and it is right — an article the
 * tenant approved is **not on their website**, and the screen's whole subject
 * is what did and did not get there.
 *
 * **The retry is `POST …/publish` again, which is the same press the row
 * offers.** There is no separate retry endpoint and there should not be: a
 * publish is idempotent at two layers (`targets.py` describes both), so
 * pressing it again on a failed document is exactly what a retry is. What the
 * alert adds is that the tenant finds the document without going looking.
 *
 * `null` when nothing failed — an alert that renders "nothing has failed" is a
 * warning-coloured box on a healthy screen, and the tenant learns to skip it.
 */
function LastFailure({
  documents,
  publishable,
  onRetried,
}: {
  documents: Document[]
  publishable: boolean
  onRetried: () => void
}): JSX.Element | null {
  const brand = useBrand()
  const toast = useToast()
  // Newest first: `list_documents` orders by `created_at DESC`, so the first
  // `failed` row is the most recent one. One is shown rather than all of them,
  // as the design draws — a column of alerts is a list, and the list is already
  // at the bottom of the screen.
  const failed = documents.find((row) => row.status === 'failed') ?? null

  const retry = useMutation((contentId: string) =>
    call(() =>
      api.POST('/api/v1/seo/content/{content_id}/publish', {
        params: {
          path: { content_id: contentId },
          header: { 'Idempotency-Key': idempotencyKey('publish', contentId) },
        },
        body: {},
      }),
    ),
  )

  if (failed === null) return null

  return (
    <div className="ar-alert ar-alert--warning" role="status">
      <span className="ar-alert__icon" aria-hidden="true">
        !
      </span>
      <div className="ar-alert__body">
        <strong>Last publish failed</strong>
        {brand.productName} could not publish{' '}
        <strong>{failed.title ?? failed.slug ?? 'an approved article'}</strong>, and kept the draft.
        Nothing was published and nothing else was attempted.
        <InlineError failure={retry.state.failure} />
        {publishable ? (
          <div className="ar-row" style={{ marginTop: 'var(--ar-space-3)' }}>
            <Button
              tone="ghost"
              size="sm"
              busy={retry.pending}
              busyLabel="Queueing"
              onClick={() => {
                void (async () => {
                  const result = await retry.run(failed.id)
                  if (!result.ok) return
                  onRetried()
                  toast.show({
                    kind: 'success',
                    title: `Queued for ${result.data.target}`,
                    body: `${brand.productName} is trying again in the background. The list below says how it went.`,
                  })
                })()
              }}
            >
              Retry
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/* --- what actually went out ---------------------------------------------------- */

/**
 * The documents that have reached a publishable state, and where each one went.
 *
 * **A failed publication is shown, deliberately.** `read_publications`' own
 * docstring makes the argument and this screen is where it lands: a `failed` row
 * is what tells a tenant the article they approved is *not* on their site, and a
 * list that waited for success would agree with a publish that never happened.
 * The mockup draws the same thing as a *"Last publish failed"* alert.
 */
function PublishedWork({
  documents,
  publishable,
  targetKind,
}: {
  documents: ReturnType<typeof useDocuments>
  publishable: boolean
  /** The site's configured target — `download` is the one whose row offers a file. */
  targetKind: string | null
}): JSX.Element {
  const rows = documents.state.data?.documents ?? []
  // A full page is the only signal that there may be more — the same reading
  // `Suppression` makes of its own `LIMIT n` with no offset.
  const capped = rows.length >= DOCUMENT_LIMIT

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>What we have published</h2>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {documents.state.status === 'loading' ? (
          <Loading label="Loading what has been approved" />
        ) : documents.state.status === 'error' ? (
          <ErrorState failure={documents.state.failure} onRetry={documents.refetch} />
        ) : rows.length === 0 ? (
          <Empty
            title="Nothing approved yet"
            body="Approved articles appear here with what happened to each of them. Until one is approved there is nothing to push."
          />
        ) : (
          rows.map((document) => (
            <DocumentRow
              key={document.id}
              document={document}
              publishable={publishable}
              targetKind={targetKind}
            />
          ))
        )}
        {capped ? (
          <p className="ar-subtle ar-text-body-sm" style={{ margin: 0 }}>
            Showing the {DOCUMENT_LIMIT} most recent, which is as many as this list will load at
            once.
          </p>
        ) : null}
      </div>
    </section>
  )
}

const DOCUMENT_STATUS: Record<string, { tone: string; label: string }> = {
  approved: { tone: 'ar-badge--info', label: 'Approved' },
  publishing: { tone: 'ar-badge--warning', label: 'Publishing' },
  published: { tone: 'ar-badge--success', label: 'Published' },
  failed: { tone: 'ar-badge--danger', label: 'Failed' },
}

const PUBLICATION_STATE: Record<string, { tone: string; label: string }> = {
  pending: { tone: 'ar-badge--warning', label: 'In progress' },
  published: { tone: 'ar-badge--success', label: 'Live' },
  failed: { tone: 'ar-badge--danger', label: 'Failed' },
  rolled_back: { tone: 'ar-badge--neutral', label: 'Withdrawn' },
}

/**
 * One approved document, and its publications on request.
 *
 * The publications are fetched when the row is opened rather than for every row
 * at once: `GET /content/{id}/publications` is per document, so a list of thirty
 * would be thirty requests to render a screen most of whose rows nobody expands.
 */
function DocumentRow({
  document,
  publishable,
  targetKind,
}: {
  document: Document
  publishable: boolean
  targetKind: string | null
}): JSX.Element {
  const brand = useBrand()
  const toast = useToast()
  const [open, setOpen] = useState(false)

  const publications = useQuery(
    (signal) =>
      open
        ? call(() =>
            api.GET('/api/v1/seo/content/{content_id}/publications', {
              params: { path: { content_id: document.id } },
              signal,
            }),
          )
        : Promise.resolve({ ok: true as const, data: null }),
    [open, document.id],
  )

  const publish = useMutation(() =>
    call(() =>
      api.POST('/api/v1/seo/content/{content_id}/publish', {
        params: {
          path: { content_id: document.id },
          header: { 'Idempotency-Key': idempotencyKey('publish', document.id) },
        },
        body: {},
      }),
    ),
  )

  const rollback = useMutation((publicationId: string) =>
    call(() =>
      api.POST('/api/v1/seo/publications/{publication_id}/rollback', {
        params: {
          path: { publication_id: publicationId },
          header: { 'Idempotency-Key': idempotencyKey('rollback', publicationId) },
        },
      }),
    ),
  )

  // SEO-UI-120. **The body comes back in the JSON and the browser saves it
  // here**, rather than the row being an `<a href>` to the route.
  // `download_content`'s own docstring carries the argument: an anchor to a
  // route that refuses renders the error envelope as a page, where every other
  // failure on this screen goes through `InlineError` like any other.
  const download = useMutation(() =>
    call(() =>
      api.GET('/api/v1/seo/content/{content_id}/download', {
        params: { path: { content_id: document.id } },
      }),
    ),
  )

  const badge = DOCUMENT_STATUS[document.status] ?? { tone: 'ar-badge--neutral', label: 'Draft' }
  const rows: Publication[] = publications.state.data?.publications ?? []

  return (
    <div className="app-pub">
      <div className="app-pub__head">
        <span className="app-pub__title">
          <strong>{document.title ?? 'Untitled draft'}</strong>
          <span className="ar-subtle">{document.slug ?? 'no slug yet'}</span>
        </span>
        <span className={`ar-badge ${badge.tone}`}>{badge.label}</span>
        <Button
          onClick={() => {
            setOpen(!open)
          }}
        >
          {open ? 'Hide history' : 'Publishing history'}
        </Button>
        {/* SEO-UI-120, on the row itself. For a *download* target the file is
            the deliverable, and a published row whose only visible buttons were
            *Publishing history* and *Publish* did not say one was there (the
            live tenant's first article, 2026-09-20). The route refuses if the
            publication was withdrawn, and that refusal reads here. */}
        {targetKind === 'download' && document.status === 'published' ? (
          <Button
            busy={download.pending}
            busyLabel="Preparing"
            onClick={() => {
              void (async () => {
                const result = await download.run(undefined)
                if (!result.ok) return
                saveTextFile(result.data.filename, result.data.body, result.data.media_type)
              })()
            }}
          >
            Download
          </Button>
        ) : null}
        {publishable ? (
          <Button
            tone="primary"
            writes
            busy={publish.pending}
            busyLabel="Queueing"
            onClick={() => {
              void (async () => {
                const result = await publish.run(undefined)
                if (!result.ok) return
                setOpen(true)
                publications.refetch()
                toast.show({
                  kind: 'success',
                  title: `Queued for ${result.data.target}`,
                  body: `${brand.productName} publishes in the background. Reopen the history in a moment to see how it went.`,
                })
              })()
            }}
          >
            Publish
          </Button>
        ) : null}
      </div>

      <InlineError
        failure={publish.state.failure ?? rollback.state.failure ?? download.state.failure}
      />

      {!open ? null : publications.state.status === 'loading' ? (
        <Loading label="Loading where this went" />
      ) : publications.state.status === 'error' ? (
        <ErrorState failure={publications.state.failure} onRetry={publications.refetch} />
      ) : rows.length === 0 ? (
        <p className="app-pub__note">
          This has never been pushed anywhere. Press <strong>Publish</strong> to send it to the
          target set up above.
        </p>
      ) : (
        <ul className="app-pub__list">
          {rows.map((row) => {
            const state = PUBLICATION_STATE[row.state] ?? {
              tone: 'ar-badge--neutral',
              label: row.state,
            }
            return (
              <li className="app-pub__row" key={row.id}>
                <span className={`ar-badge ${state.tone}`}>{state.label}</span>
                <span className="app-pub__where">
                  <strong>{row.target}</strong>
                  {row.external_url ? (
                    <a className="ar-link" href={row.external_url} rel="noreferrer noopener">
                      {row.external_url}
                    </a>
                  ) : (
                    <span className="ar-subtle">
                      {row.external_id ?? 'no address — the target did not return one'}
                    </span>
                  )}
                  <span className="ar-subtle">
                    {row.published_at ? `Live since ${formatDateTime(row.published_at, IST)}` : ''}
                    {row.rolled_back_at
                      ? `Withdrawn ${formatDateTime(row.rolled_back_at, IST)}`
                      : ''}
                    {row.attempts > 1 ? ` · ${String(row.attempts)} attempts` : ''}
                  </span>
                  {row.last_error !== null && row.last_error !== undefined ? (
                    <span className="app-pub__error">{row.last_error}</span>
                  ) : null}
                </span>
                {/* SEO-UI-120. The `download` target's whole point, and the one
                    row where the article itself is the deliverable. Offered on
                    `tenant:read` rather than on `publishable`, because reading
                    what the organisation published is what that permission is
                    for — and it is the file, not a way to change anything. */}
                {row.target === 'download' && row.state === 'published' ? (
                  <Button
                    busy={download.pending}
                    busyLabel="Preparing"
                    onClick={() => {
                      void (async () => {
                        const result = await download.run(undefined)
                        if (!result.ok) return
                        saveTextFile(result.data.filename, result.data.body, result.data.media_type)
                      })()
                    }}
                  >
                    Download
                  </Button>
                ) : null}
                {publishable && row.state === 'published' ? (
                  <Button
                    tone="quiet"
                    writes
                    busy={rollback.pending}
                    busyLabel="Withdrawing"
                    onClick={() => {
                      void (async () => {
                        const result = await rollback.run(row.id)
                        if (!result.ok) return
                        publications.refetch()
                        toast.show({
                          kind: 'success',
                          title: result.data.removed ? 'Taken down' : 'Nothing to take down',
                          body: result.data.detail,
                        })
                      })()
                    }}
                  >
                    Roll back
                  </Button>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
